import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Copy, Download, MessageCircle } from "lucide-react";

export const WhatsAppBroadcastCard = ({ products }) => {
  const active = useMemo(() => (products || []).filter((p) => p.status !== "paused"), [products]);
  const [pid, setPid] = useState("");
  const [sref, setSref] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    api.get("/rewards/share-code").then(({ data }) => setSref(data.share_code)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pid && active.length) setPid(active[0].id);
  }, [active, pid]);

  useEffect(() => {
    const p = active.find((x) => x.id === pid);
    if (!p) return;
    const link = `${process.env.REACT_APP_BACKEND_URL}/api/share/product/${p.id}${sref ? `?sref=${sref}` : ""}`;
    const desc = (p.description || "").slice(0, 120);
    setText(
      `🔥 *${p.title}* — $${(p.price || 0).toFixed(2)}\n` +
      `${desc}${(p.description || "").length > 120 ? "…" : ""}\n` +
      `✅ In stock — ${p.stock} available\n` +
      `👉 Order here: ${link}\n` +
      `⚡ Sold on VISMARKS`
    );
  }, [pid, sref, active]);

  if (active.length === 0) return null;
  const current = active.find((x) => x.id === pid);

  return (
    <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="whatsapp-broadcast-card">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-[#25D366]" />
        <h2 className="font-bold text-lg">WhatsApp group poster</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400">
        Ready-made broadcast for your groups &amp; status. The link auto-shows your branded product photo card in WhatsApp — or download it to attach yourself.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <select
          data-testid="broadcast-product-select"
          value={pid}
          onChange={(e) => setPid(e.target.value)}
          className="bg-black/40 border border-[#27272A] rounded-lg px-3 h-10 text-sm sm:w-64"
        >
          {active.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
      <Textarea
        data-testid="broadcast-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        className="mt-3 bg-black/40 border-[#27272A] text-sm font-mono"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          data-testid="broadcast-whatsapp-btn"
          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener")}
          className="rounded-full bg-[#25D366] hover:bg-[#1fb457] text-black h-10 px-6 font-bold"
        >
          <MessageCircle className="h-4 w-4 mr-2" /> Post to WhatsApp
        </Button>
        <Button
          data-testid="broadcast-copy-btn"
          variant="outline"
          onClick={() => { navigator.clipboard.writeText(text); toast.success("Broadcast copied — paste into any group"); }}
          className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-10 px-5"
        >
          <Copy className="h-4 w-4 mr-2" /> Copy text
        </Button>
        {current && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/share/product/${current.id}/card.png`} target="_blank" rel="noreferrer" data-testid="broadcast-image-link">
            <Button variant="outline" className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-10 px-5">
              <Download className="h-4 w-4 mr-2" /> Photo card
            </Button>
          </a>
        )}
      </div>
    </div>
  );
};
